import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Camera, Calendar, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { AutoTranslate } from '../../hooks/useAutoTranslate';
import { getAlbums } from '../../services/api';

const getLocalized = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.sv || obj.en || Object.values(obj)[0] || '';
};

const formatEventDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'sv' ? 'sv-SE' : lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Homepage section that surfaces the 3 latest published photo albums.
 * Sits between "Ur vår katalog" and "Dagens läsningar".
 * Auto-hides when there are zero published albums.
 */
const AlbumCarouselSection = () => {
  const { language, isRTL } = useLanguage();
  const [albums, setAlbums] = useState(null); // null = loading, [] = empty

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAlbums();
        if (!cancelled) setAlbums((data || []).slice(0, 3));
      } catch (err) {
        if (!cancelled) setAlbums([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entirely while loading or when empty
  if (!albums || albums.length === 0) return null;

  return (
    <section
      className="py-14"
      style={{ background: 'linear-gradient(135deg, #f7f1e6 0%, #f2ead9 100%)' }}
      data-testid="homepage-album-carousel"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 gap-3">
          <div>
            <div className={`flex items-center gap-2 text-amber-800 text-sm font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Camera className="w-4 h-4" />
              <AutoTranslate>Församlingens minnen</AutoTranslate>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900">
              <AutoTranslate>Fotoalbum</AutoTranslate>
            </h2>
          </div>
          <Link to="/forsamling/album" data-testid="home-see-all-albums">
            <Button
              variant="outline"
              className="border-amber-700 text-amber-900 hover:bg-amber-700 hover:text-white transition-colors"
            >
              <AutoTranslate>Se alla album</AutoTranslate>
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map((album) => (
            <Link
              key={album.id}
              to={`/forsamling/album/${album.id}`}
              data-testid={`home-album-card-${album.id}`}
              className="block group"
            >
              <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-xl bg-white h-full">
                <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                  {album.cover_image_url ? (
                    <img
                      src={album.cover_image_url}
                      alt={getLocalized(album.title, language)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <ImageIcon className="w-16 h-16" />
                    </div>
                  )}
                  <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1`}>
                    <ImageIcon className="w-3 h-3" />
                    {album.photo_count || 0}
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-serif font-bold text-gray-900 mb-2 line-clamp-2 hyphens-auto break-words">
                    {getLocalized(album.title, language)}
                  </h3>
                  <div className={`flex items-center text-sm text-gray-500 gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{formatEventDate(album.event_date, language)}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AlbumCarouselSection;
